import { toInteger } from 'lodash';
import { getFramework } from '@/framework';
import { base64Kit } from '@/utils/base64-kit';
import {
    createCanvas,
    createForeignObjectImageUrl,
    downScaleCanvas,
    exportSize,
    fetchVectorText,
    getDrawContainParams,
    loadImageAsync,
    serializerHtmlToString,
    serializeSvgText,
} from '@/utils/file-kit';
import { deleteExtraInfo } from '@/utils/layer-kit';
import { pxToInch } from '@/utils/unit-kit';
import {
    IExportBehavior,
    ILayerBehavior,
    ILayerOriginInfo,
    ILayerProps,
    IMAGE_LAYER_CANVAS_ID_PREFIX,
    IMediaLayerProps,
    IRenderLayerExtraProps,
    ISize,
    LayerType,
    MediaType,
} from '@/version-types';
import MediaLayer from './views/media-layer';

function getBehavior(identity: string) {
    const layerBehavior: ILayerBehavior = {
        render(layer: ILayerProps, extraProps: IRenderLayerExtraProps) {
            return <MediaLayer layer={layer} {...extraProps} />;
        },
        async savePPTLayerAsync(slide, layer, stageWidth, stageHeight) {
            const { left, top, width, height, rotate, data } = layer;
            const options = {
                // path: url,
                x: pxToInch(left),
                y: pxToInch(top),
                w: pxToInch(width),
                h: pxToInch(height),
                rotate: rotate > 0 ? rotate : 360 + rotate,
                data: '',
            };

            const canvas = await layerBehavior.getResultCanvasAsync?.(
                {
                    ...layer,
                    rotate: 0,
                },
                {
                    width: stageWidth,
                    height: stageHeight,
                },
            );

            if (!canvas) {
                return;
            }

            const { canvas: resultCanvas } = createCanvas(width, height);
            const ctx = resultCanvas.getContext('2d');
            ctx?.drawImage(canvas, left, top, width, height, 0, 0, width, height);
            options.data = resultCanvas?.toDataURL() || '';

            slide.addImage(options);
        },
        async getResultCanvasAsync(layer: ILayerProps, resultSize: ISize): Promise<HTMLCanvasElement | undefined> {
            // console.time('===========生成时间');
            const { width, height, left, top, opacity, rotate } = layer;
            const { width: stageWidth, height: stageHeight } = resultSize;
            const resultWidth = toInteger(width);
            const resultHeight = toInteger(height);
            const centerPoint = {
                x: resultWidth / 2 + left,
                y: resultHeight / 2 + top,
            };
            const drawOffsetPoint = {
                x: -resultWidth / 2,
                y: -resultHeight / 2,
            };
            const layerImage = await layerBehavior.getLayerImageAsync?.(layer, false);
            if (!layerImage) return;
            const { canvas: destCanvas, ctx: destCtx } = createCanvas(stageWidth, stageHeight);
            destCtx.translate(centerPoint.x, centerPoint.y);
            destCtx.rotate((Math.PI / 180) * rotate);

            // 透明度
            destCtx.globalAlpha = opacity;

            destCtx.drawImage(
                layerImage,
                0,
                0,
                resultWidth,
                resultHeight,
                drawOffsetPoint.x,
                drawOffsetPoint.y,
                resultWidth,
                resultHeight,
            );
            // console.timeEnd('===========生成时间');
            return destCanvas;
        },
        // needScaleCanvas 当原图超过一定大小的时候是否压缩
        async getLayerImageAsync(
            layer: ILayerProps,
            globalAlpha = true,
            needScaleCanvas = true,
            noNeedBorderRadius = false,
        ) {
            // console.time('===========生成时间');
            const { width, height, opacity } = layer;
            const {
                url,
                maskURL,
                mediaType,
                imageTransform: {
                    flipH,
                    flipV,
                    scale,
                    rotate: imgRotate,
                    widthRatio,
                    heightRatio,
                    offsetX: offsetXRatio,
                    offsetY: offsetYRatio,
                },
                naturalHeight: domHeight,
                naturalWidth: domWidth,
                poster,
            } = layer.data as IMediaLayerProps;

            let baseImage: HTMLCanvasElement | HTMLImageElement = document.getElementById(
                `${IMAGE_LAYER_CANVAS_ID_PREFIX}${layer.id}`,
            ) as HTMLCanvasElement;

            // 等待上传完成
            await base64Kit.getRemoteUrlAsync(url);

            // 设置绘制的真正元素
            if (!baseImage || baseImage?.getAttribute('ready') === 'false') {
                try {
                    const loadUrl = mediaType === MediaType.Video ? poster : url;
                    if (!loadUrl) return createCanvas(1, 1).canvas;
                    baseImage = await loadImageAsync(loadUrl, mediaType !== MediaType.Video);
                    const { canvas } = createCanvas(baseImage.width, baseImage.height, baseImage);
                    baseImage = canvas;
                } catch (e) {
                    const framework = getFramework(identity);
                    framework.events.emitBehaviorErrorImage({ layerId: layer.id, imageUrl: url });
                    return createCanvas(1, 1).canvas;
                }
            }

            const scaleCanvas = parseFloat(Math.floor(width / domWidth).toFixed(2));
            if (needScaleCanvas && scaleCanvas < 0.5 && widthRatio === 1 && heightRatio === 1 && scale === 1) {
                //没有使用过裁剪功能
                console.time(`图片压缩处理${layer.id}`);
                baseImage = downScaleCanvas(baseImage as HTMLCanvasElement, width, height);
                console.timeEnd(`图片压缩处理${layer.id}`);
            }

            const resultWidth = toInteger(width);
            const resultHeight = toInteger(height);
            const offsetX = toInteger(offsetXRatio * widthRatio * width * scale);
            const offsetY = toInteger(offsetYRatio * heightRatio * height * scale);
            const originImgWidth = toInteger(width * widthRatio);
            const originImgHeight = toInteger(height * heightRatio);
            let imgOffsetX = resultWidth / 2;
            let imgOffsetY = resultHeight / 2;
            if (flipH) {
                imgOffsetX = imgOffsetX - offsetX;
            } else {
                imgOffsetX = imgOffsetX + offsetX;
            }
            if (flipV) {
                imgOffsetY = imgOffsetY - offsetY;
            } else {
                imgOffsetY = imgOffsetY + offsetY;
            }
            // 将图层绘制到画布上
            const getLayerResult = async (layerCanvas: HTMLCanvasElement) => {
                // 绘制蒙版
                const { canvas: maskCanvas, ctx: maskCtx } = createCanvas(resultWidth, resultHeight);
                if (maskURL) {
                    let finalUrl = maskURL;
                    if (/\.svg$/.test(maskURL)) {
                        const svgMaskText = await fetchVectorText(maskURL);
                        const { width, height } = exportSize(svgMaskText);
                        finalUrl = createForeignObjectImageUrl(
                            Math.floor(width),
                            Math.floor(height),
                            serializerHtmlToString(serializeSvgText(svgMaskText)),
                            true,
                        );
                    }
                    const maskImg = await loadImageAsync(finalUrl);
                    const res = getDrawContainParams(maskImg.width, maskImg.height, resultWidth, resultHeight);
                    maskCtx.drawImage(maskImg, res.dx, res.dy, res.dw, res.dh);
                    maskCtx.globalCompositeOperation = 'source-in';
                    maskCtx.drawImage(layerCanvas, 0, 0);
                }
                const finalCanvas = maskURL ? maskCanvas : layerCanvas;
                if (!globalAlpha) return finalCanvas;
                const { canvas: destCanvas, ctx: destCtx } = createCanvas(finalCanvas.width, finalCanvas.height);
                destCtx.globalAlpha = opacity;
                destCtx.drawImage(finalCanvas, 0, 0, finalCanvas.width, finalCanvas.height);
                return destCanvas;
            };

            //  绘制图层
            const getLayerCanvas = async () => {
                const { canvas: destCanvas, ctx: destCtx } = createCanvas(resultWidth, resultHeight);
                destCtx.save();
                destCtx.translate(imgOffsetX, imgOffsetY);

                let rotate = imgRotate;

                if (flipH) {
                    rotate = -rotate;
                }

                if (flipV) {
                    rotate = -rotate;
                }

                destCtx.rotate((rotate * Math.PI) / 180);
                destCtx.scale(scale * (flipH ? -1 : 1), scale * (flipV ? -1 : 1));
                destCtx.drawImage(
                    baseImage,
                    -originImgWidth / 2,
                    -originImgHeight / 2,
                    originImgWidth,
                    originImgHeight,
                );
                destCtx.restore();
                return destCanvas;
            };

            const layerCanvas = await getLayerCanvas();

            const result = await getLayerResult(layerCanvas);

            return result;
        },

        getScaleMinRatio(originInfo: ILayerOriginInfo, layer: ILayerProps): number {
            const { width, cutWidth } = originInfo;
            const offWidth = width - cutWidth;
            return (width - offWidth) / width;
        },
        getScaleIsNeedLimitSize(): boolean {
            return true;
        },
        whenGroupCanResize(layer: ILayerProps) {
            const horizontal = true,
                vertical = true;
            return { horizontal, vertical };
        },

        async onBeforeSaveAsync(layer: ILayerProps, isAutoSave?: boolean) {
            deleteExtraInfo(layer);
        },
    };
    return layerBehavior;
}

const exportBehavior: IExportBehavior = {
    layerType: LayerType.MEDIA_LAYER,
    getBehavior,
};

export default exportBehavior;
